import { Shipment } from '../../models/shipment';
import { createSelector } from '@ngrx/store';
import { AppState } from '../index';
import { Violation } from '../../models/violation';
import { selectJournalBusyShipments } from '../journal/journal.state';
import { ShipmentState as ShipmentStateUtil } from '../../utils/shipment-state';
import { selectCourierOptions } from '../profile/profile.state';
import { shipmentHash } from '../../utils/shipment-hash';
import { ShipmentCategoryEnum } from '../../enum/shipment-category.enum';
import { PackagingTypeEnum } from '../../enum/packaging-type.enum';
import { ShipmentCommodity } from '../../models/shipment-commodity';
import { OptionTagEnum } from '../../enum/option-tag.enum';
import { ValidationMode } from '../../enum/validation-mode.enum';
import { ShipmentOption } from '../../enum/shipment-option.enum';

export interface ShipmentEditorState {
    id: string;
    shipment: Shipment;
    undoStack: Shipment[];
    redoStack: Shipment[];
    ignoreValueHash: string;
    undo: Shipment;
    quickQuote: boolean;
    saving: boolean;
    loading: boolean;
    violations: Violation[];
    savingHash: string;
    savedHash: string;
    changedAt: number;
    savedAt: number;
    displayQuotes: boolean;
    validationMode: ValidationMode;
    deletingDocumentIds: string[];
    boxPackingBusy: boolean;
    boxPackingSucceeded: boolean;
    boxPackingFailed: string;
}

/* here we get the unique group numbers.  For { ref1:"A", ref2:"B" } we want [ "1", "2" ] */
function groupsFromReferences(references: any[string]): string[] {
    const output = [];
    for (const key in references) {
        if (references.hasOwnProperty(key)) {
            const index = key.replace(/[\w]+([0-9]+)$/, '$1');
            if (output.indexOf(index) === -1) {
                output.push(index);
            }
        }
    }
    return output;
}

export const selectShipmentEditorState = (state: AppState) => state.shipmentEditor;

export const selectShipmentEditorId = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.id);

export const selectShipmentEditorShipment = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment);

export const selectShipmentEditorOptions = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.courierOptions);

export const selectShipmentEditorHasOption = (option: string) => createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => !!state.shipment.courierOptions[option]);

export const selectShipmentEditorMetric = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.weightUnit === 'KG');

export const selectShipmentEditorReferences = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.references);

export const selectShipmentEditorReferenceGroups = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => groupsFromReferences(state.shipment.references));

export const selectShipmentEditorOptionIds = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => Object.keys(state.shipment.courierOptions));

export const selectShipmentEditorValid = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.id === state.shipment.id);

export const selectShipmentEditorSaving = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.saving);

export const selectShipmentEditorCurrentHash = createSelector(selectShipmentEditorShipment, (shipment) => shipmentHash(shipment));

export const selectShipmentEditorSavedHash = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.savedHash);

export const selectShipmentEditorSavingHash = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.savingHash);

export const selectShipmentEditorCurrency = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.currency);

export const selectShipmentEditorNeedsSave = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => {
        const currentHash = shipmentHash(state.shipment);
        if (state.saving && state.savingHash) {
            return true;
        }
        return !!state.savedHash && state.savedHash !== currentHash;
    });

export const selectShipmentEditorUndoAvailable = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.undoStack.length > 0);

export const selectShipmentEditorRedoAvailable = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.redoStack.length > 0);

export const selectShipmentEditorInternational = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => !!state.shipment.shipper && !!state.shipment.destination &&
        !!state.shipment.shipper.country && !!state.shipment.destination.country &&
        state.shipment.shipper.country !== state.shipment.destination.country);

export const selectShipmentEditorDeclaration = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => !!state.shipment.shipper && !!state.shipment.destination &&
        !!state.shipment.shipper.country && !!state.shipment.destination.country &&
        state.shipment.shipper.country !== state.shipment.destination.country && !state.shipment.documentsOnly);

export const selectQuickQuote = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.quickQuote);

export const selectShipmentEditorShipper = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.shipper);

export const selectShipmentEditorDestination = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.destination);

export const selectShipmentEditorBroker = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.broker);

export const selectShipmentEditorSoldTo = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.soldTo);

export const selectShipmentEditorPaymentOption = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.paymentOption);

export const selectShipmentEditorPaymentAccount = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.paymentAccountNumber);

export const selectShipmentEditorViolations = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.violations);

export const selectShipmentEditorLoading = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.loading);

export const selectShipmentEditorUnsaved = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.changedAt ? (state.savedAt ? state.savedAt < state.changedAt : true) : false);

export const selectShipmentEditorChangedAt = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.changedAt);

export const selectShipmentEditorSavedAt = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.savedAt);

export const selectShipmentEditorSaveDelta = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => (state.changedAt || 0) - (state.savedAt || 0));

export const selectShipmentEditorDisplayQuotes = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.displayQuotes);

export const selectShipmentEditorActionsBusy = createSelector(
    selectShipmentEditorId,
    selectJournalBusyShipments,
    (id, shipments) => shipments.indexOf(id) > -1);

export const selectShipmentEditable = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => ShipmentStateUtil.isQuotable(state.shipment.statusCode)
);

export const selectShipmentEditorActiveOptionDefinitions = createSelector(
    selectShipmentEditorOptions,
    selectCourierOptions,
    (shipmentOptions, courierOptions) =>
        courierOptions
            .filter(o => o.tags.includes(OptionTagEnum.SELECTABLE))
            .filter(o => shipmentOptions[o.code])
);

export const selectShipmentEditorHasInsurance = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.packages.filter(p => p.value > 0).length > 0
);

export const selectShipmentEditorInsuranceAmount = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.packages.reduce((total, pkg) => total + pkg.value * pkg.quantity, 0)
);

export const selectShipmentEditorInsuranceCategory = createSelector(
    selectShipmentEditorOptions,
    (options) => options[ShipmentOption.INSURANCE_CATEGORY] ?? 'GENERAL'
);

export const selectCanRecordUndo = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => shipmentHash(state.undo) !== shipmentHash(state.shipment)
);

export const selectShipmentEditorPackagingType = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.packagingType
);

export const selectShipmentEditorPackages = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.packages
);

export const selectShipmentEditorCategory = createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.packagingType === PackagingTypeEnum.PALLET
        ? ShipmentCategoryEnum.LTL : ShipmentCategoryEnum.PARCEL
);

export const selectShipmentEditorAllCommodities = createSelector(
    selectShipmentEditorPackages,
    (packages): ShipmentCommodity[] =>
        packages.flatMap((pkg, packageIndex) =>
            (pkg.commodities ?? []).map(
                (commodity): ShipmentCommodity => ({
                    ...commodity,
                    packageIndex,
                })
            )
        )
);

export const selectShipmentEditorValidationMode = createSelector(
    selectShipmentEditorState, (state: ShipmentEditorState) => state.validationMode
);

export const selectShipmentEditorDate = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.shipmentDate
);

export const selectShipmentEditorInsuranceProvisionId = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.insuranceProvisionId
);

export const selectShipmentEditorViolationsForPath = (path: string) => createSelector(
    selectShipmentEditorState,
    (state: ShipmentEditorState) => state.violations.filter(v => v.path.indexOf(path) === 0)
);

export const selectShipmentEditorDocuments = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.shipment.documents ?? []
);

export const selectShipmentEditorDeletingDocumentIds = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.deletingDocumentIds ?? []
);

export const shipmentEditorBoxPackingBusy = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.boxPackingBusy);

export const shipmentEditorBoxPackingSucceeded = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.boxPackingSucceeded);

export const shipmentEditorBoxPackingFailed = createSelector(selectShipmentEditorState,
    (state: ShipmentEditorState) => state.boxPackingFailed);
